import {Tooltip} from '@mui/material'
import {Box, Loading, Skeleton, Typography} from '@viptech/react-components'
import {useEffect, useRef, useState} from 'react'
import ReactApexChart from 'react-apexcharts'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'

type ColumnChartData = {
  name: string
  data: Array<number | null>
  color?: string
}

type ColumnChartProps = {
  title: string
  loading: boolean
  data: Array<ColumnChartData>
  labelsXaxis: Array<string | number | Array<string>> | undefined
  height?: string
  refetching?: boolean
  enableDataLabels?: boolean
  subtitle?: string
}

const GAP = 52

const MOCK_SIZES = [
  60, 44, 47, 89, 146, 26, 142, 86, 83, 135, 115, 28, 149, 52, 43, 93, 80, 48, 100, 37, 144, 145, 109, 71, 83, 102, 26, 135, 150, 116, 70, 103, 149, 49, 133,
  78, 85, 64, 29, 85, 110, 132, 31, 101, 134, 44, 122, 125, 123, 45,
]

function DashboardColumnChartsCard({title, loading, refetching, data, height = '320px', labelsXaxis, enableDataLabels, subtitle}: ColumnChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - GAP)
      }
    }

    updateContainerWidth()

    window.addEventListener('resize', updateContainerWidth)
    const observer = new ResizeObserver(updateContainerWidth)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateContainerWidth)
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth - GAP)
    }
  }, [loading, refetching])

  const maxValue = Math.max(...data.flatMap((item) => item.data).filter((value): value is number => value !== null))
  const opts: ApexCharts.ApexOptions = {
    series: data,
    chart: {
      type: 'bar',
      fontFamily: "'Inter', 'sans-serif",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        distributed: false,
        dataLabels: {position: 'top'},
      },
    },
    dataLabels: {
      enabled: enableDataLabels === true,
      textAnchor: 'middle',
      offsetY: -20,
      offsetX: 0,
      style: {
        colors: ['#054399', '#9F0B35', '#FDB022'],
      },
    },
    legend: {
      show: true,
      markers: {
        shape: 'circle',
      },
      itemMargin: {
        horizontal: 10,
      },
    },
    xaxis: {
      categories: labelsXaxis ?? [],
      labels: {
        rotate: 0,
        trim: true,
        style: {
          fontSize: '12px',
        },
      },
    },
    responsive: [
      {
        breakpoint: 968,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '80%',
            },
          },
        },
      },
    ],
    grid: {
      show: true,
    },
    yaxis: {
      min: 0,
      max: maxValue,
    },
    tooltip: {
      enabled: false,
      theme: 'light',
      y: {
        title: {
          formatter: (val) => val,
        },
      },
    },
    noData: {
      text: 'Nenhum dado foi encontrado',
      align: 'center',
      verticalAlign: 'top',
      offsetX: 5,
      offsetY: 15,
      style: {
        color: '#8E8E8E',
        fontSize: '16px',
        fontFamily: "'Inter', 'sans-serif",
      },
    },
  }

  const {styledTheme} = usePersonalizedThemeByCompany()

  return (
    <Box
      ref={containerRef}
      position='relative'
      backgroundColor='white'
      borderRadius='16px'
      width='100%'
      height={height}
      style={{boxShadow: '0px 5px 8px 0px #00000026'}}>
      <Box display='flex' direction='column' padding='24px' rowGap='6px' align='center'>
        <Typography variant='span' size='16px' color={styledTheme.dashboard.colors.title} children={title} weight={600} />
        {subtitle && (
          <Typography
            variant='span'
            size='12px'
            style={{fontStyle: 'italic'}}
            color={styledTheme.dashboard.colors.title}
            children={subtitle?.replace('.', '')}
          />
        )}

        {refetching && !loading && (
          <Tooltip title='Buscando atualizações' placement='top'>
            <Box position='absolute' right='24px' display='flex' columnGap='8px'>
              <Loading size='20px' />
            </Box>
          </Tooltip>
        )}

        {loading ? (
          <Box width={'100%'} height={'200px'} display='flex' align='flex-end' columnGap='24px' paddingRight='24px' backgroundColor='white' overflow='hidden'>
            <Box>
              {new Array(10).fill(0).map((_, index) => (
                <Skeleton width='12px' height='8px' borderRadius='4px' key={index} />
              ))}
            </Box>
            {new Array(30).fill(0).map((_, index) => (
              <Skeleton width='48px' height={`${MOCK_SIZES[index]}px`} borderRadius='4px' key={index} />
            ))}
          </Box>
        ) : (
          <ReactApexChart options={opts} series={opts.series} type='bar' height={220} width={containerWidth} />
        )}
      </Box>
    </Box>
  )
}

export default DashboardColumnChartsCard
